import { useState, useEffect, useCallback } from 'react';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import PublishStatusCard from '@/components/admin/PublishStatusCard';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Briefcase,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  MoreHorizontal,
  GripVertical,
  Star,
  Check,
  X,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  Database
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const APPROVAL_BADGES = {
  draft: { label: 'Draft', color: 'bg-gray-500' },
  pending_review: { label: 'Pending Review', color: 'bg-amber-500' },
  approved: { label: 'Approved', color: 'bg-green-500' },
  changes_requested: { label: 'Changes Requested', color: 'bg-orange-500' },
  rejected: { label: 'Rejected', color: 'bg-red-500' }
};

export const ServicesManager = () => {
  const { token, user, logout } = useCMSAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [search, setSearch] = useState('');
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    short_description: '',
    full_description: '',
    icon: '',
    features: '',
    is_featured: false,
    is_active: true,
    order: 0
  });

  const handleUnauthorized = (res) => {
    if (res.status === 401) {
      toast.error('Session expired. Please log in again.');
      logout();
      return true;
    }
    return false;
  };

  const fetchServices = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      
      const res = await fetch(`${API_URL}/api/cms/content/services?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (handleUnauthorized(res)) return;
      
      if (res.ok) {
        const data = await res.json();
        setServices(data.services);
      }
    } catch (e) {
      toast.error('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  }, [token, search, logout]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleSave = async () => {
    if (!formData.title || !formData.short_description || !formData.full_description) {
      toast.error('Title and descriptions are required');
      return;
    }
    
    setFormLoading(true);
    try {
      const payload = {
        ...formData,
        features: formData.features ? formData.features.split('\n').filter(f => f.trim()) : [],
        order: parseInt(formData.order) || 0
      };
      
      const url = selectedService 
        ? `${API_URL}/api/cms/content/services/${selectedService.id}`
        : `${API_URL}/api/cms/content/services`;
      
      const res = await fetch(url, {
        method: selectedService ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success(selectedService ? 'Service updated' : 'Service created');
        setDialogOpen(false);
        resetForm();
        fetchServices();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (e) {
      toast.error('Failed to save service');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedService) return;
    
    setFormLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/content/services/${selectedService.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Service deleted');
        setDeleteDialogOpen(false);
        setSelectedService(null);
        fetchServices();
      } else {
        toast.error('Failed to delete');
      }
    } catch (e) {
      toast.error('Failed to delete service');
    } finally {
      setFormLoading(false);
    }
  };

  const toggleActive = async (service) => {
    try {
      const res = await fetch(`${API_URL}/api/cms/content/services/${service.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !service.is_active })
      });
      
      if (res.ok) {
        toast.success(service.is_active ? 'Service deactivated' : 'Service activated');
        fetchServices();
      }
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const openEditDialog = (service) => {
    setSelectedService(service);
    setFormData({
      title: service.title,
      short_description: service.short_description,
      full_description: service.full_description,
      icon: service.icon || '',
      features: service.features?.join('\n') || '',
      is_featured: service.is_featured || false,
      is_active: service.is_active !== false,
      order: service.order?.toString() || '0'
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      short_description: '',
      full_description: '',
      icon: '',
      features: '',
      is_featured: false,
      is_active: true,
      order: services.length.toString()
    });
    setSelectedService(null);
  };

  const handleSeedData = async () => {
    if (!confirm('This will replace all existing services with seed data. Continue?')) return;
    
    setSeeding(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/content/services/seed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Services seeded successfully!');
        fetchServices();
      } else {
        toast.error('Failed to seed data');
      }
    } catch (error) {
      console.error('Error seeding:', error);
      toast.error('Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  const canEdit = ['super_admin', 'admin', 'content_approver', 'content_editor'].includes(user?.role);
  const canDelete = ['super_admin', 'admin', 'content_approver'].includes(user?.role);
  const canApprove = ['super_admin', 'admin', 'content_approver'].includes(user?.role);

  const handleApprovalAction = async (serviceId, action, comment = null) => {
    try {
      const res = await fetch(`${API_URL}/api/cms/content/services/${serviceId}/approval`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action, comment })
      });
      
      if (res.ok) {
        const actionLabels = {
          submit_for_review: 'Submitted for review',
          approve: 'Approved',
          reject: 'Rejected',
          request_changes: 'Changes requested'
        };
        toast.success(actionLabels[action] || 'Action completed');
        fetchServices();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Action failed');
      }
    } catch (e) {
      toast.error('Failed to perform action');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="services-manager">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ux-charcoal flex items-center gap-2">
              <Briefcase className="w-7 h-7 text-ux-blue" />
              Services
            </h1>
            <p className="text-ux-charcoal/60">
              Manage service offerings displayed on the website
            </p>
          </div>
          
          {canEdit && (
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={handleSeedData}
                disabled={seeding}
              >
                {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
                Seed Data
              </Button>
              <Button 
                className="bg-ux-blue hover:bg-ux-blue/90"
                onClick={() => { resetForm(); setDialogOpen(true); }}
                data-testid="add-service-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Service
              </Button>
            </div>
          )}
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ux-charcoal/40" />
              <Input
                placeholder="Search services..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Services Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Order</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Features</TableHead>
                  <TableHead className="text-center">Featured</TableHead>
                  <TableHead className="text-center">Active</TableHead>
                  {canEdit && <TableHead className="w-16">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {services.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-ux-charcoal/60">
                      No services found
                    </TableCell>
                  </TableRow>
                ) : (
                  services.map((service) => (
                    <TableRow key={service.id} className={!service.is_active ? 'opacity-50' : ''}>
                      <TableCell className="text-center">
                        <span className="text-ux-charcoal/60">{service.order || 0}</span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{service.title}</p>
                          <p className="text-xs text-ux-charcoal/60">
                            {service.short_description?.substring(0, 80)}...
                          </p>
                          {service.approval_status && service.approval_status !== 'approved' && (
                            <Badge className={`${APPROVAL_BADGES[service.approval_status]?.color} text-white text-xs mt-1`}>
                              {APPROVAL_BADGES[service.approval_status]?.label}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-ux-charcoal/60">
                          {service.features?.length || 0} features
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {service.is_featured ? (
                          <Star className="w-5 h-5 text-amber-500 fill-amber-500 mx-auto" />
                        ) : (
                          <Star className="w-5 h-5 text-gray-300 mx-auto" />
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {canEdit ? (
                          <Switch
                            checked={service.is_active !== false}
                            onCheckedChange={() => toggleActive(service)}
                          />
                        ) : (
                          service.is_active !== false ? (
                            <Check className="w-5 h-5 text-green-500 mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-red-500 mx-auto" />
                          )
                        )}
                      </TableCell>
                      {canEdit && (
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDialog(service)}>
                                <Edit className="w-4 h-4 mr-2" /> Edit
                              </DropdownMenuItem>
                              
                              {/* Approval workflow actions */}
                              {(!service.approval_status || service.approval_status === 'draft' || service.approval_status === 'changes_requested') && (
                                <DropdownMenuItem 
                                  onClick={() => handleApprovalAction(service.id, 'submit_for_review')}
                                  className="text-blue-600"
                                >
                                  <Send className="w-4 h-4 mr-2" /> Submit for Review
                                </DropdownMenuItem>
                              )}
                              
                              {canApprove && service.approval_status === 'pending_review' && (
                                <>
                                  <DropdownMenuItem 
                                    onClick={() => handleApprovalAction(service.id, 'approve')}
                                    className="text-green-600"
                                  >
                                    <CheckCircle className="w-4 h-4 mr-2" /> Approve
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => {
                                      const comment = prompt('Enter reason for requesting changes:');
                                      if (comment) handleApprovalAction(service.id, 'request_changes', comment);
                                    }}
                                    className="text-orange-600"
                                  >
                                    <Clock className="w-4 h-4 mr-2" /> Request Changes
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => {
                                      const comment = prompt('Enter reason for rejection:');
                                      if (comment) handleApprovalAction(service.id, 'reject', comment);
                                    }}
                                    className="text-red-600"
                                  >
                                    <XCircle className="w-4 h-4 mr-2" /> Reject
                                  </DropdownMenuItem>
                                </>
                              )}
                              
                              {canDelete && (
                                <DropdownMenuItem 
                                  onClick={() => { setSelectedService(service); setDeleteDialogOpen(true); }}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedService ? 'Edit Service' : 'New Service'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 md:col-span-1">
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(d => ({ ...d, title: e.target.value }))}
                    placeholder="e.g. University Selection"
                  />
                </div>
                <div className="col-span-2 md:col-span-1">
                  <Label>Icon (Lucide icon name or URL)</Label>
                  <Input
                    value={formData.icon}
                    onChange={(e) => setFormData(d => ({ ...d, icon: e.target.value }))}
                    placeholder="e.g. GraduationCap"
                  />
                </div>
              </div>
              <div>
                <Label>Short Description * (max 200 chars)</Label>
                <Textarea
                  value={formData.short_description}
                  onChange={(e) => setFormData(d => ({ ...d, short_description: e.target.value }))}
                  placeholder="Brief description for cards/lists"
                  rows={2}
                />
              </div>
              <div>
                <Label>Full Description *</Label>
                <Textarea
                  value={formData.full_description}
                  onChange={(e) => setFormData(d => ({ ...d, full_description: e.target.value }))}
                  placeholder="Detailed service description..."
                  rows={5}
                />
              </div>
              <div>
                <Label>Features (one per line)</Label>
                <Textarea
                  value={formData.features}
                  onChange={(e) => setFormData(d => ({ ...d, features: e.target.value }))}
                  placeholder="Expert guidance&#10;Personalized matching&#10;Application support"
                  rows={4}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData(d => ({ ...d, order: e.target.value }))}
                    placeholder="0"
                  />
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(v) => setFormData(d => ({ ...d, is_featured: v }))}
                  />
                  <Label>Featured</Label>
                </div>
              </div>
              <PublishStatusCard
                isPublished={formData.is_active}
                onChange={(v) => setFormData(d => ({ ...d, is_active: v }))}
                entityLabel="service"
                publicRoute="/services"
                testId="service-publish-toggle"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={formLoading}>
                {formLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {selectedService ? 'Save Changes' : 'Create Service'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete Service</DialogTitle>
            </DialogHeader>
            <p>Are you sure you want to delete "{selectedService?.title}"?</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} disabled={formLoading}>
                {formLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ServicesManager;
